import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Signal,
  WritableSignal,
  signal,
} from '@angular/core';
import { Building, Floor } from '@miq/domain/pv';
import {
  ElevatorToSegmentAdapterServiceToken,
  PVElevatorToVerticalSegmentAdapter,
} from './property-elevator/property-elevator.adapter';
import { PVColumnResolver } from './floor-info/column.resolver';
import { PVColumn } from './shared/column.model';
import { EXPORT_RESOLVER_TOKEN } from '../stacking-plan-container/actions-toolbar/export/export-resolvers/export-resolver.model';
import { PVExportResolver } from '../stacking-plan-container/actions-toolbar/export/export-resolvers/pv-export-resolver';
import { PVExportConfigGetter } from '../stacking-plan-container/actions-toolbar/export/export-config-getters/pv-export-config-getter';
import { DefaultTableSelectionHandler, TABLE_SELECTOR_HANDLER } from '@cbre/components';
import { Observable, Subject, delay, merge, tap } from 'rxjs';
import { PVFloorSpaceOverlayHelper } from './floor-space-overlay.helper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControl } from '@angular/forms';
import { StackPlanViewStateService } from '../state/sp-view-state.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { UserRole, hasOneOfTheRoles } from '@miq/user';
import { StackingPlanService } from '../stacking-plan.service';
import { StackingPlanReviewer } from '../stacking-plan.model';
import { PVFloorSpaceDataHelper } from './floor-space-details/spaces-details/spaces-details.helper';
import {
  FLOOR_SPACE_DETAILS_STATE_HANDLER_TOKEN,
  FloorSpaceDetailsHandlerSrv,
  FloorSpaceDetailsStateHandlerService,
} from './floor-space-details/floor-space-details-state-handler.service';
import { PVExportPDFService } from '../stacking-plan-container/actions-toolbar/export/export-resolvers/pv-export-pdf';

@UntilDestroy()
@Component({
  selector: 'sp-property-view',
  templateUrl: './property-view.component.html',
  styleUrls: ['./property-view.component.scss'],
  providers: [
    { provide: ElevatorToSegmentAdapterServiceToken, useClass: PVElevatorToVerticalSegmentAdapter },
    { provide: EXPORT_RESOLVER_TOKEN, useClass: PVExportResolver },
    PVColumnResolver,
    PVExportConfigGetter,
    { provide: TABLE_SELECTOR_HANDLER, useClass: DefaultTableSelectionHandler },
    PVFloorSpaceDataHelper,
    { provide: FLOOR_SPACE_DETAILS_STATE_HANDLER_TOKEN, useClass: FloorSpaceDetailsStateHandlerService },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('showHideBagde', [
      transition(':enter', [
        style({ opacity: 0, width: 0, color: 'white' }),
        animate('0.5s ease-in-out', style({ opacity: 1, width: '100px', color: '#666' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, width: '100px', color: '#666' }),
        animate('0.5s ease-in-out', style({ opacity: 0, width: 0, color: 'white' })),
      ]),
    ]),
  ],
  standalone: false,
})
export class PropertyViewComponent implements OnInit, OnDestroy {
  propertyViewData = signal<Building>({ floors: [], building_info: { number_elevator_bank: 0 } } as Building);
  columns = signal<PVColumn[]>([]);
  exportOptions = signal(null);
  #hoveredFloor: Floor = null;
  showSpaceDetails$: Observable<boolean>;
  spacesDetailsCompPostion: Record<string, any> = null;
  spaceDetailsFreeze = signal(false);

  unaccountedToggleCtrl = new FormControl<boolean>(false);
  nonRentableToggleCtrl = new FormControl<boolean>(false);
  smoothToggleCtrl = new FormControl<boolean>(false);

  lastReviewedData$: Observable<StackingPlanReviewer>;

  @Input() set building(building: Building) {
    this.propertyViewData.set(building);
  }

  #showDetails = new Subject<number>();
  showDetails$ = this.#showDetails
    .asObservable()
    .pipe(untilDestroyed(this), delay(1000))
    .subscribe(floorId => {
      if (
        this.#hoveredFloor != null &&
        floorId === this.#hoveredFloor?.floor_id &&
        !this.fsOverlayCoordinates.isVisible()
      ) {
        this.fsOverlayCoordinates.updateVisibility(true);
      }
    });
  hasEditionPerms = signal(false);

  selectedFloor: WritableSignal<Floor> = signal(null);
  buildingFloorIdPrefix = 'building-floor--';
  elevatorFloorIdPrefix = 'elevator-floor--';
  tableFloorRowIdPrefix = 'table-row-floor--';
  spaceDetailsFloorIdPrefix = 'space-details-floor';
  isPvReportDownloading: Signal<boolean>;

  constructor(
    private columnResolver: PVColumnResolver,
    private exportConfigSrv: PVExportConfigGetter,
    private floorSpaceData: PVFloorSpaceDataHelper,
    private fsOverlayCoordinates: PVFloorSpaceOverlayHelper,
    private spViewState: StackPlanViewStateService,
    private stackingPlanService: StackingPlanService,
    private pvExportService: PVExportPDFService,
    @Inject(FLOOR_SPACE_DETAILS_STATE_HANDLER_TOKEN) private floorSpaceStateHandler: FloorSpaceDetailsHandlerSrv
  ) {}

  #hasEditionPerms = () => {
    const user = this.stackingPlanService.resolveUser();
    return (
      this.stackingPlanService.isTrustedUser() &&
      hasOneOfTheRoles(user, [UserRole.USResearcher, UserRole.DandT, UserRole.DIGUser])
    );
  };

  ngOnInit() {
    this.lastReviewedData$ = this.stackingPlanService.getLastReviewedData();
    this.showSpaceDetails$ = this.fsOverlayCoordinates.isVisible$;
    this.exportOptions.set(this.exportConfigSrv.getExportOptions());
    this.#setColumns();
    this.fsOverlayCoordinates.positionCoordinates$
      .pipe(tap(coords => (this.spacesDetailsCompPostion = coords)))
      .subscribe();

    this._listenUnaccountedNonRentableToggles();
    this.hasEditionPerms.set(this.#hasEditionPerms());
    this.isPvReportDownloading = this.pvExportService.isPvReportDownloading;
  }

  private _listenUnaccountedNonRentableToggles() {
    // init control values
    const state = this.spViewState.getState();
    this.unaccountedToggleCtrl.setValue(state.showUnaccountedSpace);
    this.nonRentableToggleCtrl.setValue(state.showNonRentableSpace);
    this.smoothToggleCtrl.setValue(state.isSmoothView);

    const unaccountedChanges$ = this.unaccountedToggleCtrl.valueChanges.pipe(
      tap(val => this.spViewState.setShowUnaccountedSpace(val))
    );
    const nonRentableChanges$ = this.nonRentableToggleCtrl.valueChanges.pipe(
      tap(value => this.spViewState.setShowNonRentableSpace(value))
    );
    const smoothChanges$ = this.smoothToggleCtrl.valueChanges.pipe(tap(value => this.spViewState.setSmoothView(value)));
    // listen to changes
    merge(unaccountedChanges$, nonRentableChanges$, smoothChanges$).pipe(untilDestroyed(this)).subscribe();
  }

  getFloorStyleByRecordsType(floor: Floor) {
    const NRA_Records =
      floor.floor_non_rentable_area > 0 &&
      floor.floor_tgt_available_area == 0 &&
      floor.floor_tgt_occupied_area == 0 &&
      floor.floor_non_tgt_area == 0;
    const Only_Lease_OR_Availability_Records =
      floor.floor_non_tgt_area > 0 && floor.floor_tgt_available_area == 0 && floor.floor_tgt_leased_area == 0;
    return NRA_Records || Only_Lease_OR_Availability_Records;
  }

  #setColumns() {
    this.columnResolver
      .resolve()
      .pipe(tap(cols => this.columns.set(cols)))
      .subscribe();
  }

  cleanHoveredFloor(removeFreeze?: boolean) {
    if (this.spaceDetailsFreeze() && !removeFreeze) {
      return;
    }
    this.fsOverlayCoordinates.updateVisibility(false);
    this.#hoveredFloor = null;
    this.spaceDetailsFreeze.set(false);
    this.floorSpaceData.setData(null);
  }

  onClickFloor(evt: MouseEvent, floor: Floor) {
    if (this.selectedFloor()?.floor_id != null && this.selectedFloor().floor_id === floor.floor_id) {
      if (this.fsOverlayCoordinates.isVisible() && this.spaceDetailsFreeze()) {
        this.selectedFloor.set(null);
        this.cleanHoveredFloor(true);
      } else {
        this.#hoveredFloor = floor;
        this.fsOverlayCoordinates.updateVisibility(true);
        this.spaceDetailsFreeze.set(true);
        this.#updatePosition(evt);
      }
    } else {
      // When the Overlay is visible but the user clicks a different floor then the overlay should be updated
      this.selectedFloor.set(floor);
      this.floorSpaceData.setData(floor);
      this.#hoveredFloor = floor;
      this.fsOverlayCoordinates.updateVisibility(true);
      this.spaceDetailsFreeze.set(true);
      this.#updatePosition(evt);
      this.floorSpaceStateHandler.resetState();
    }
  }

  setHoveredFloor(evt: MouseEvent, floor: Floor) {
    // When there is a selected row then the onhovered data should only be updated for the selected floor
    // until the user unselect the floor
    if (
      (this.selectedFloor() == null &&
        (this.#hoveredFloor == null || floor?.floor_id !== this.#hoveredFloor?.floor_id)) ||
      this.selectedFloor()?.floor_id == floor.floor_id
    ) {
      this.#hoveredFloor = floor;
      this.fsOverlayCoordinates.setEvent(evt);
      this.floorSpaceData.setData(floor);
    }

    if (!this.fsOverlayCoordinates.isVisible()) {
      if (this.#hoveredFloor != null) {
        this.#showDetails.next(this.#hoveredFloor.floor_id);
      }
    }
  }

  #updatePosition(evt: MouseEvent) {
    this.fsOverlayCoordinates.setEvent(evt);
    this.fsOverlayCoordinates.updatePosition();
  }

  ngOnDestroy() {
    this.floorSpaceData.unsubscribe();
  }

  #isOutsideTheBuildingArea(floorId: number, evt: MouseEvent & { path?: HTMLElement[] }) {
    const evtPath = evt?.composedPath() || evt.path || [];
    const sectionIdsByFloor = [
      `${this.buildingFloorIdPrefix}${floorId}`,
      `${this.tableFloorRowIdPrefix}${floorId}`,
      this.spaceDetailsFloorIdPrefix,
    ];
    return !evtPath.some((htmlEle: HTMLElement) => sectionIdsByFloor.includes(htmlEle.id));
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(evt: MouseEvent & { path?: HTMLElement[] }) {
    if (this.fsOverlayCoordinates.isVisible() && !this.fsOverlayCoordinates.shouldKeepOpen()) {
      if (!this.spaceDetailsFreeze()) {
        this.#updatePosition(evt);
      }

      // If the modal is freezed then keep the modal open if the user is moving the cursor over the same floor
      // Or inside the pop-over
      const floorId = this.#hoveredFloor?.floor_id || this.selectedFloor()?.floor_id;
      if (this.#isOutsideTheBuildingArea(floorId, evt)) {
        this.cleanHoveredFloor(true);
      }
    }
  }

  @HostListener('document:scroll', ['$event'])
  onScroll() {
    if (this.fsOverlayCoordinates.isVisible() && !this.spaceDetailsFreeze()) {
      this.fsOverlayCoordinates.updateVisibility(false);
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(evt: MouseEvent & { path?: HTMLElement[] }) {
    if (this.selectedFloor() == null) {
      return;
    }
    if (this.#isOutsideTheBuildingArea(this.selectedFloor().floor_id, evt)) {
      this.selectedFloor.set(null);
      this.cleanHoveredFloor(true);
    }
  }
}
